/**
 * The data layer as the store sees it: Promise-returning calls, because in the browser they run in the data worker
 * (`createDataClient()` from '@nanome-lite/data/client'). Only that subpath is imported at runtime, and lazily, so
 * Molstar lives in the worker chunk and never in the main bundle; types still come from the package root.
 */
import type { ComponentInput, FilterModel } from './dto';
import type { DefaultComponentsResult, EntryComposition, FilterContext, LoadOptions, LoadedEntry, NanomeModel } from '@nanome-lite/data';

export interface DataClient {
  loadEntry(bytes: Uint8Array, fileName: string, options?: LoadOptions): Promise<LoadedEntry>;
  /** Switches the frame on the main-thread model and, when worker-backed, on the worker's copy too. */
  setFrame(model: NanomeModel, modelSerial: number): Promise<NanomeModel>;
  evaluateFilters(filters: FilterModel[], model: NanomeModel, ctx: FilterContext): Promise<Uint32Array>;
  defaultComponents(entrySerial: number, model: NanomeModel, options?: { pocketDistance?: number }): Promise<ComponentInput[]>;
  /** Default components plus the AddDefaultComponents.cs hidden flags (Water, Ion). */
  defaultComponentsDetailed(entrySerial: number, model: NanomeModel, options?: { pocketDistance?: number }): Promise<DefaultComponentsResult>;
  composition?(entrySerial: number, name: string, model: NanomeModel): Promise<EntryComposition>;
  fileMetadata?(bytes: Uint8Array, ext: string): Promise<Record<string, string>>;
  /** Drop a model from the worker's registry once its entry is removed. */
  release?(model: NanomeModel): Promise<void>;
  dispose?(): void;
}

export class DataLayerUnavailableError extends Error {
  override readonly name = 'DataLayerUnavailableError';
  constructor(detail?: string) { super(detail ? `data layer not available: ${detail}` : 'data layer not available'); }
}

let override: DataClient | null = null;
let cached: Promise<DataClient> | null = null;

/** Inject a client (tests, or an app that constructs the worker-backed client itself). `null` restores auto-detection. */
export function setDataClient(client: DataClient | null): void {
  override = client;
  cached = null;
}

/** Resolve the worker-backed data client once (in-thread where `Worker` is undefined, e.g. Node). */
export function getDataClient(): Promise<DataClient> {
  if (override) return Promise.resolve(override);
  if (!cached) {
    cached = import('@nanome-lite/data/client')
      .then((mod) => mod.createDataClient())
      .catch((e: unknown) => { cached = null; throw new DataLayerUnavailableError(e instanceof Error ? e.message : String(e)); });
  }
  return cached;
}

/** True when a client was injected (tests); the lazily created client is not reported here. */
export function hasDataClient(): boolean { return override !== null; }
